import { useMutation, useQueryClient } from "@tanstack/react-query"
import posthog from "posthog-js"
import { isHttpError } from "@/shared/api/errors"
import { retryTransient } from "@/shared/config/query"
import { captureError } from "@/shared/lib/diagnostics"
import { triggerImpact } from "@/shared/lib/platform"
import { streamKeys } from "../model/keys"
import type { StreamMessage } from "../model/types"
import {
	appendStreamMessage,
	isStreamMessageStarred,
	patchStreamMessage,
	removeStreamMessage,
} from "./cache"
import { sendStreamMessage, starStreamMessage } from "./client"

export function useSendStreamMessage() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: sendStreamMessage,
		onSuccess: (saved) => {
			appendStreamMessage(queryClient, saved)
			posthog.capture("stream_message_sent", { length: saved.text.length })
		},
		onError: (error) => captureError(error, { scope: "stream.send" }),
	})
}

function patchStar(delta: 1 | -1): (message: StreamMessage) => StreamMessage {
	const starred = delta > 0
	return (message) => {
		if (message.starred === starred) return message
		return {
			...message,
			stars_count: Math.max(message.stars_count + delta, 0),
			starred,
		}
	}
}

export function useStarStreamMessage() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: starStreamMessage,
		retry: retryTransient,
		onMutate: async (messageId: number) => {
			await queryClient.cancelQueries({ queryKey: streamKeys.messages() })
			const applied = !isStreamMessageStarred(queryClient, messageId)
			if (applied) {
				triggerImpact("medium")
				patchStreamMessage(queryClient, messageId, patchStar(1))
			}
			return { applied }
		},
		onSuccess: (saved) => {
			patchStreamMessage(queryClient, saved.id, () => saved)
			posthog.capture("stream_message_starred", { message_id: saved.id })
		},
		onError: (error, messageId, context) => {
			if (isHttpError(error) && error.status === 404) {
				void removeStreamMessage(queryClient, messageId)
				return
			}
			if (context?.applied) {
				patchStreamMessage(queryClient, messageId, patchStar(-1))
			}
			captureError(error, { scope: "stream.star", message_id: messageId })
		},
	})
}
